/* eslint-disable */
// DnaModalShell — New-DNA creation-modal shell. Backdrop (warm blur fade) +
// card (entrance) + header (diamond glyph · title · live subtitle · × close) +
// top required-progress sliver + scrollable body + sticky footer (required
// meter · ghost Cancel · amber-pill primary). FOUR close affordances:
// × button, backdrop click, ESC key, Cancel button. Applies .createDnaRoot so
// all descendants inherit the scoped tokens (Convention #68).
import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import shell from './createDna.module.css';
import styles from './DnaModalShell.module.css';
import DnaConfirm from './DnaConfirm';

export default function DnaModalShell({
  open,
  onClose,
  title,
  subtitle,
  requiredMet = 0,
  requiredTotal = 0,
  onSubmit,
  submitLabel = 'Create',
  submitting = false,
  submitDisabled = false,
  isMobile = false,
  isDirty = false,
  // Optional header glyph. Defaults to the diamond every existing caller gets,
  // so DevFormModal2 and NewItemModal are untouched. Added for the announcement
  // composer, whose identity is the megaphone (header button, card eyebrow,
  // this dialog) — shipping a diamond there would break that thread.
  glyph = null,
  children,
  overlay = null,
  'data-test': dataTest,
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const backdropRef = useRef(null);
  const downOnBackdrop = useRef(false);

  // Dirty guard: any close affordance (×, Cancel, ESC, backdrop) confirms first
  // when the form has unsaved input; otherwise closes immediately.
  const requestClose = () => { if (isDirty) setConfirmOpen(true); else onClose && onClose(); };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (confirmOpen) { setConfirmOpen(false); return; } // ESC on the confirm = keep editing
      requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose, confirmOpen, isDirty]);

  if (!open) return null;

  const pct = requiredTotal > 0 ? Math.round((requiredMet / requiredTotal) * 100) : 0;
  const allMet = requiredTotal > 0 && requiredMet >= requiredTotal;

  return createPortal(
    <div className={shell.createDnaRoot}>
      <div
        ref={backdropRef}
        className={styles.backdrop}
        onMouseDown={(e) => { downOnBackdrop.current = (e.target === backdropRef.current); }}
        onMouseUp={(e) => {
          // Only dismiss when BOTH the press and release land on the backdrop
          // itself — kills the "drag started inside the card, released on the
          // backdrop" false close.
          if (downOnBackdrop.current && e.target === backdropRef.current) requestClose();
          downOnBackdrop.current = false;
        }}
      >
        <div
          className={`${styles.card} ${isMobile ? styles.cardMobile : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          data-test={dataTest}
        >
          {/* Required-progress sliver (top edge) */}
          {requiredTotal > 0 && (
            <div className={styles.sliverTrack} aria-hidden="true">
              <div className={styles.sliverFill} style={{ width: pct + '%' }} />
            </div>
          )}

          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headLeft}>
              <span className={styles.diamond} aria-hidden="true">{glyph || '◆'}</span>
              <div className={styles.headText}>
                <div className={styles.title}>{title}</div>
                {subtitle != null && <div className={styles.subtitle}>{subtitle}</div>}
              </div>
            </div>
            <button type="button" className={styles.close} onClick={requestClose} aria-label="Close">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className={styles.body}>{children}</div>

          {/* Footer */}
          <div className={styles.footer}>
            {requiredTotal > 0 && (
              <div className={`${styles.meter} ${allMet ? styles.meterDone : ''}`}>
                <span className={styles.meterDot} aria-hidden="true" />
                <span>{requiredMet} / {requiredTotal} required</span>
              </div>
            )}
            <div className={styles.footerBtns}>
              <button type="button" className={styles.ghost} onClick={requestClose}>Cancel</button>
              <button
                type="button"
                className={styles.primary}
                onClick={onSubmit}
                disabled={submitting || submitDisabled}
              >
                {submitting ? 'Saving…' : submitLabel}
              </button>
            </div>
          </div>

          {/* In-card overlay slot (e.g. MissingFieldsPopup) — direct child of
              .card so it covers header + body + footer, above the scroll area. */}
          {overlay}

          {/* Unsaved-changes dirty guard (modal-on-modal; sits above overlay) */}
          {confirmOpen && (
            <DnaConfirm onDiscard={onClose} onCancel={() => setConfirmOpen(false)} />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
